import React, {ChangeEvent} from "react";
import {Paper} from "@mui/material";
import {EnterNewPasswordProps, NewPasswordTo} from "./types";
import {ValidatedPasswordConfirmedInput} from "./PasswordInput/ValidatedPasswordConfirmedInput";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {usePasswordValidation} from "./PasswordInput/usePasswordValidation";
import {LoadingButton} from "@mui/lab";
import {ValidatedPasswordInputWithCriteriaBox} from "./PasswordInput/ValidatedPasswordInputWithCriteriaBox";
import ErrorBanner from "../../common/ErrorBanner";
import ForgottenPasswordCancelLink from "./ForgottenPasswordCancelLink";
import {ValidationInputCheckMark} from "./PasswordInput/ValidationInputCheckMark";
import {useLxTranslations} from "@lxo-blocks/react";
import {FORGOT_PASSWORD_FORM_TRANSLATION} from "./ForgotPasswordForm.translation.de";

export default function EnterNewPasswordForm(enterNewPasswordProps: EnterNewPasswordProps) {
    const {t} = useLxTranslations(FORGOT_PASSWORD_FORM_TRANSLATION);
    const {
        validatePassword,
        validateConfirmedPassword,
        isPasswordConfirmed,
    } = usePasswordValidation();

    const formMethods = useForm<NewPasswordTo>({
        mode: "all",
    });
    const {handleSubmit, formState, control, reset, getValues} = formMethods;
    const {isSubmitting, errors} = formState;

    const requiredMessage = t("ERROR_PASSWORD_NOT_VALID");
    const requiredMessageConfirmedPassword = t("ERROR_PASSWORD_NOT_VALID");

    return (
        <>
            <Paper sx={{px: 8, py: 5}}>
                {enterNewPasswordProps.currentError.length > 0 && (

                    <ErrorBanner headline={t("PASSWORD_RESET_ERROR_HEADLINE")}
                                 description={enterNewPasswordProps.currentError.length > 0
                                     ? enterNewPasswordProps.currentError
                                     : ""}
                                 sx={{mb: 3}}/>)}
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(enterNewPasswordProps.submitMethod)}>
                        <Controller
                            name="password1"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: requiredMessage,
                                validate: validatePassword,
                            }}
                            render={(props: any) => (
                                <ValidatedPasswordInputWithCriteriaBox
                                    sx={{mb: 1}}
                                    {...props}
                                    error={!!errors.password1}
                                    helperText={
                                        errors.password1?.message ? errors.password1.message : ""
                                    }
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                        props.onChange(event);
                                        reset({...getValues(), password2: ""});
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="password2"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: requiredMessageConfirmedPassword,
                                validate: validateConfirmedPassword,
                            }}
                            render={(props: any) => (
                                <>
                                    <ValidatedPasswordConfirmedInput
                                        sx={{mb: 1}}
                                        {...props}
                                        error={!!errors.password2}
                                        helperText={
                                            errors.password2?.message ? errors.password2.message : ""
                                        }
                                    />
                                    {isPasswordConfirmed && (
                                        <ValidationInputCheckMark/>
                                    )}
                                </>
                            )}
                        />
                        <LoadingButton
                            sx={{py: 1}}
                            variant="contained"
                            color="primary"
                            type="submit"
                            fullWidth
                            disabled={isSubmitting || !!errors.password1 || !!errors.password2 || !isPasswordConfirmed}
                            loading={isSubmitting}
                            loadingPosition="end"
                            endIcon={<></>}
                        >
                            {isSubmitting
                                ? t("ENTER_NEW_PASSWORD_AND_LOGIN_IS_SUBMITTING")
                                : t("ENTER_NEW_PASSWORD_AND_LOGIN")}
                        </LoadingButton>
                    </form>
                </FormProvider>
            </Paper>
            <ForgottenPasswordCancelLink sx={{pt: 3, pb: 1}}/>
        </>
    );
}
